import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { store } from '@/ui/store';
import { useStore } from '@/ui/useStore';
import { quoteSheetName, rowColToA1, toggleReferenceAt } from '@/model/address';
import { resolveAddress, sheetById } from '@/model/workbook';

export function FormulaBar(): JSX.Element {
  useStore();
  const sel = store.sel;
  const committed = store.activeRawText();
  const [draft, setDraft] = useState(committed);
  const [dirty, setDirty] = useState(false);
  const [nameBox, setNameBox] = useState(rowColToA1(sel.row, sel.col));
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Only set right after F4 rewrites `draft` — the caret has to be moved once
  // the DOM has the new value, which a plain assignment here would predate.
  const [caretAfterToggle, setCaretAfterToggle] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (caretAfterToggle === null) return;
    inputRef.current?.setSelectionRange(caretAfterToggle, caretAfterToggle);
    setCaretAfterToggle(null);
  }, [caretAfterToggle, draft]);

  // Follow the selection unless the user is mid-edit in the bar itself. While
  // a cell editor is open the bar mirrors it, so pointing at a reference shows
  // the formula growing in both places.
  const editing = store.editing;
  useEffect(() => {
    if (!dirty) setDraft(editing ? editing.text : committed);
    setNameBox(rowColToA1(sel.row, sel.col));
  }, [committed, sel.row, sel.col, dirty, editing]);

  const commit = (move: 'down' | 'none'): void => {
    store.setCellRaw(sel.row, sel.col, draft);
    setDirty(false);
    if (move === 'down') store.select(sel.row + 1, sel.col);
  };

  /*
   * While an edit is open the box names the cell being EDITED, not the one
   * under the pointer. Pointing at a reference moves the selection, and a name
   * box that follows it says the formula is going somewhere it is not — it is
   * qualified with the sheet whenever the pointing has left home.
   */
  const away = editing && editing.sheetId !== store.wb.activeSheetId;
  const label = editing
    ? `${away ? `${quoteSheetName(sheetById(store.wb, editing.sheetId)?.name ?? '?')}!` : ''}${rowColToA1(editing.row, editing.col)}`
    : sel.r1 === sel.r2 && sel.c1 === sel.c2
      ? nameBox
      : `${rowColToA1(sel.r1, sel.c1)}:${rowColToA1(sel.r2, sel.c2)}`;

  return (
    <div className="gt-formulabar">
      <input
        className="gt-namebox"
        value={label}
        onChange={(e) => setNameBox(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          const rc = resolveAddress(nameBox);
          if (rc) store.select(rc.row, rc.col);
        }}
        spellCheck={false}
        aria-label="Cell reference"
      />
      <span className="gt-fx">fx</span>

        <input
          ref={inputRef}
          className="gt-formula-input"
          value={draft}
          spellCheck={false}
          placeholder="Type a value, or = to start a formula"
          onChange={(e) => {
            setDraft(e.target.value);
            setDirty(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit('down');
              // Back to the grid, so the arrow keys move the selection again
              // instead of the text caret — same as Excel's formula bar.
              store.focusGrid();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setDraft(committed);
              setDirty(false);
              store.focusGrid();
            } else if (e.key === 'F4') {
              // Same cycle as the in-cell editor (model/address.ts), but this
              // bar never goes through store.editing — it keeps its own caret.
              e.preventDefault();
              const at = inputRef.current?.selectionStart ?? draft.length;
              const next = toggleReferenceAt(draft, at);
              if (!next) return;
              setDraft(next.text);
              setDirty(true);
              setCaretAfterToggle(next.caret);
            }
          }}
          onBlur={() => {
            if (dirty) commit('none');
          }}
        />
    </div>
  );
}
